import { useSuspenseQuery, type MutationFunction } from "@tanstack/react-query";
import { Flex, PrimarySecondaryPage } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import { OnboardingStepper } from "onboarding";

import { ManagementAccountsUploadSupportingContent } from "../../../components";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { DocumentNavigationGroup } from "../components";
import { BalanceSheetDocumentUpload } from "../components/balance-sheet-document-upload";

export const BalanceSheetDocumentUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: uploadedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplicationData.id);
  const { mutate: triggerValidation } = useTriggerValidation(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.BALANCE_SHEET
  );

  const filteredDocumentTypes = uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.BALANCE_SHEET
  );
  const filteredFiles = filteredDocumentTypes?.flatMap(({ data }) => data);
  const hasUploadedDocuments = Boolean(filteredFiles?.length);

  const primaryContent = (
    <Flex direction="column" gap="6">
      <BalanceSheetDocumentUpload
        fundingApplication={fundingApplicationData}
        mutate={fundingApplicationMutation.mutate as MutationFunction<FormattedFundingApplicationScreenUpdate>}
        accountingThreshold={getAccountingThreshold(businessModel)}
        hasUploadedDocuments={hasUploadedDocuments}
      />
      <DocumentNavigationGroup
        isStepComplete={
          fundingApplicationMutation.variables?.accountingOptedOut ||
          fundingApplicationData.accountingOptedOut ||
          (hasUploadedDocuments && Boolean(fundingApplicationData.accountingCurrency))
        }
        forType={UploadDocumentType.BALANCE_SHEET}
        onPrimaryAction={() => filteredFiles?.length && triggerValidation(filteredFiles.map(({ id }) => id))}
      />
    </Flex>
  );

  return (
    <PrimarySecondaryPage
      titleSlot={<OnboardingStepper currentStep={4} />}
      showStepperBackground={true}
      title={intl.formatMessage({
        id: "funding-application: upload balance sheet",
        defaultMessage: "Upload your Balance Sheet",
      })}
      primaryContent={primaryContent}
      secondaryContent={<ManagementAccountsUploadSupportingContent />}
    />
  );
};
